/**
 * Theme Creator → Music: step tracker grid (16 steps/bar, multi-bar, chords).
 */
import {
  DEFAULT_THEME_MUSIC,
  THEME_MUSIC_NOTE_ROWS,
  THEME_MUSIC_OSCILLATORS,
  THEME_MUSIC_STEPS_PER_BAR,
  type ThemeMusicData,
  type ThemeMusicOscillator,
  type ThemeMusicScope,
} from "./theme-types";
import {
  ThemeMusicPreviewPlayer,
  themeMusicNoteLabel,
  themeMusicTotalSteps,
} from "./theme-music";

export type ThemeMusicEditorApi = {
  getMusic: () => ThemeMusicData;
  setMusic: (music: ThemeMusicData) => void;
  destroy: () => void;
};

function cellKey(note: number, step: number): string {
  return `${note}:${step}`;
}

function cloneMusic(m: ThemeMusicData): ThemeMusicData {
  return {
    ...m,
    cells: m.cells.map((c) => ({ ...c })),
  };
}

export function mountThemeMusicEditor(
  root: HTMLElement,
  opts: {
    getMusic: () => ThemeMusicData;
    setMusic: (music: ThemeMusicData) => void;
  },
): ThemeMusicEditorApi {
  const box = document.createElement("div");
  box.className = "modeBox adminThemeMusicBox";

  const title = document.createElement("h4");
  title.className = "adminPlayerStatsSubheading";
  title.textContent = "Music";
  box.appendChild(title);

  const hint = document.createElement("p");
  hint.className = "accessHint";
  hint.textContent =
    "Compose a chip loop on the step grid. It saves with this theme and replaces arcade music while the theme is active (players can still mute via music volume or turn off Show Admin Theme).";
  box.appendChild(hint);

  const controls = document.createElement("div");
  controls.className = "adminThemeMusicControls";
  box.appendChild(controls);

  const enableLab = document.createElement("label");
  enableLab.className = "adminThemeDontChange";
  const enableCb = document.createElement("input");
  enableCb.type = "checkbox";
  enableLab.appendChild(enableCb);
  enableLab.appendChild(document.createTextNode("Enable theme music"));
  controls.appendChild(enableLab);

  const bpmLab = document.createElement("label");
  bpmLab.className = "adminThemeMusicField";
  bpmLab.innerHTML = `<span>BPM</span>`;
  const bpmInput = document.createElement("input");
  bpmInput.type = "number";
  bpmInput.min = "40";
  bpmInput.max = "240";
  bpmInput.step = "1";
  bpmInput.className = "formInput";
  bpmLab.appendChild(bpmInput);
  controls.appendChild(bpmLab);

  const oscLab = document.createElement("label");
  oscLab.className = "adminThemeMusicField";
  oscLab.innerHTML = `<span>Oscillator</span>`;
  const oscSelect = document.createElement("select");
  oscSelect.className = "formInput";
  for (const o of THEME_MUSIC_OSCILLATORS) {
    const opt = document.createElement("option");
    opt.value = o;
    opt.textContent = o;
    oscSelect.appendChild(opt);
  }
  oscLab.appendChild(oscSelect);
  controls.appendChild(oscLab);

  const scopeLab = document.createElement("label");
  scopeLab.className = "adminThemeMusicField";
  scopeLab.innerHTML = `<span>Plays in</span>`;
  const scopeSelect = document.createElement("select");
  scopeSelect.className = "formInput";
  for (const [v, label] of [
    ["both", "Menu + Game"],
    ["menu", "Menu only"],
    ["game", "Game only"],
  ] as const) {
    const opt = document.createElement("option");
    opt.value = v;
    opt.textContent = label;
    scopeSelect.appendChild(opt);
  }
  scopeLab.appendChild(scopeSelect);
  controls.appendChild(scopeLab);

  const barsLab = document.createElement("label");
  barsLab.className = "adminThemeMusicField";
  barsLab.innerHTML = `<span>Bars</span>`;
  const barsRow = document.createElement("div");
  barsRow.className = "adminThemeMusicBarsRow";
  const barsVal = document.createElement("span");
  barsVal.className = "adminThemeMusicBarsVal";
  const barsMinus = document.createElement("button");
  barsMinus.type = "button";
  barsMinus.className = "btn";
  barsMinus.textContent = "−";
  const barsPlus = document.createElement("button");
  barsPlus.type = "button";
  barsPlus.className = "btn";
  barsPlus.textContent = "+";
  barsRow.appendChild(barsMinus);
  barsRow.appendChild(barsVal);
  barsRow.appendChild(barsPlus);
  barsLab.appendChild(barsRow);
  controls.appendChild(barsLab);

  const transport = document.createElement("div");
  transport.className = "adminThemeMusicTransport";
  const playBtn = document.createElement("button");
  playBtn.type = "button";
  playBtn.className = "btn green";
  playBtn.textContent = "Play";
  const clearBtn = document.createElement("button");
  clearBtn.type = "button";
  clearBtn.className = "btn";
  clearBtn.textContent = "Clear notes";
  transport.appendChild(playBtn);
  transport.appendChild(clearBtn);
  controls.appendChild(transport);

  const gridWrap = document.createElement("div");
  gridWrap.className = "adminThemeMusicGridWrap";
  const grid = document.createElement("div");
  grid.className = "adminThemeMusicGrid";
  gridWrap.appendChild(grid);
  box.appendChild(gridWrap);

  root.appendChild(box);

  const player = new ThemeMusicPreviewPlayer();
  let painting: boolean | null = null;

  function music(): ThemeMusicData {
    const m = opts.getMusic();
    return m ? cloneMusic(m) : cloneMusic(DEFAULT_THEME_MUSIC);
  }

  function commit(next: ThemeMusicData) {
    opts.setMusic(next);
    if (player.playing) player.updateMusic(next);
    renderGrid();
    syncControls();
  }

  function syncControls() {
    const m = music();
    enableCb.checked = m.enabled;
    bpmInput.value = String(m.bpm);
    oscSelect.value = m.oscillator;
    scopeSelect.value = m.scope;
    barsVal.textContent = String(m.bars);
    playBtn.textContent = player.playing ? "Pause" : "Play";
    playBtn.classList.toggle("green", !player.playing);
  }

  function renderGrid() {
    const m = music();
    const total = themeMusicTotalSteps(m);
    const active = new Set(m.cells.map((c) => cellKey(c.note, c.step)));
    const playStep = player.playing ? player.currentStep : -1;

    grid.style.setProperty("--theme-music-cols", String(total));
    grid.style.setProperty("--theme-music-rows", String(THEME_MUSIC_NOTE_ROWS));
    grid.replaceChildren();

    // Corner spacer
    const corner = document.createElement("div");
    corner.className = "adminThemeMusicCorner";
    grid.appendChild(corner);

    for (let s = 0; s < total; s++) {
      const head = document.createElement("div");
      head.className =
        "adminThemeMusicStepHead" +
        (s % THEME_MUSIC_STEPS_PER_BAR === 0 ? " adminThemeMusicStepHead--bar" : "") +
        (s === playStep ? " adminThemeMusicStepHead--play" : "");
      head.textContent = String((s % THEME_MUSIC_STEPS_PER_BAR) + 1);
      grid.appendChild(head);
    }

    for (let note = 0; note < THEME_MUSIC_NOTE_ROWS; note++) {
      const lab = document.createElement("div");
      lab.className =
        "adminThemeMusicNoteLab" + (note % 12 === 0 ? " adminThemeMusicNoteLab--c" : "");
      lab.textContent = themeMusicNoteLabel(note);
      grid.appendChild(lab);

      for (let step = 0; step < total; step++) {
        const cell = document.createElement("button");
        cell.type = "button";
        cell.className =
          "adminThemeMusicCell" +
          (step % THEME_MUSIC_STEPS_PER_BAR === 0 ? " adminThemeMusicCell--bar" : "") +
          (active.has(cellKey(note, step)) ? " adminThemeMusicCell--on" : "") +
          (step === playStep ? " adminThemeMusicCell--play" : "");
        cell.dataset.note = String(note);
        cell.dataset.step = String(step);
        cell.setAttribute("aria-label", `${themeMusicNoteLabel(note)} step ${step + 1}`);
        cell.setAttribute("aria-pressed", active.has(cellKey(note, step)) ? "true" : "false");
        grid.appendChild(cell);
      }
    }
  }

  function toggleCell(note: number, step: number, force?: boolean) {
    const m = music();
    const key = cellKey(note, step);
    const has = m.cells.some((c) => c.note === note && c.step === step);
    const turnOn = force != null ? force : !has;
    if (turnOn && has) return;
    if (!turnOn && !has) return;
    if (turnOn) {
      m.cells.push({ note, step });
      m.enabled = true;
    } else {
      m.cells = m.cells.filter((c) => !(c.note === note && c.step === step));
    }
    commit(m);
  }

  grid.addEventListener("pointerdown", (ev) => {
    const t = (ev.target as HTMLElement).closest(".adminThemeMusicCell") as HTMLElement | null;
    if (!t) return;
    ev.preventDefault();
    const note = Number(t.dataset.note);
    const step = Number(t.dataset.step);
    if (!Number.isFinite(note) || !Number.isFinite(step)) return;
    const m = music();
    const has = m.cells.some((c) => c.note === note && c.step === step);
    painting = !has;
    toggleCell(note, step, painting);
    try {
      grid.setPointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
  });
  grid.addEventListener("pointermove", (ev) => {
    if (painting == null) return;
    const el = document.elementFromPoint(ev.clientX, ev.clientY);
    const t = el instanceof HTMLElement ? el.closest(".adminThemeMusicCell") : null;
    if (!(t instanceof HTMLElement)) return;
    const note = Number(t.dataset.note);
    const step = Number(t.dataset.step);
    if (!Number.isFinite(note) || !Number.isFinite(step)) return;
    toggleCell(note, step, painting);
  });
  const endPaint = () => {
    painting = null;
  };
  grid.addEventListener("pointerup", endPaint);
  grid.addEventListener("pointercancel", endPaint);
  grid.addEventListener("lostpointercapture", endPaint);

  enableCb.onchange = () => {
    const m = music();
    m.enabled = enableCb.checked;
    commit(m);
  };
  bpmInput.onchange = () => {
    const m = music();
    m.bpm = Math.max(40, Math.min(240, Math.round(Number(bpmInput.value) || 120)));
    commit(m);
  };
  oscSelect.onchange = () => {
    const m = music();
    m.oscillator = oscSelect.value as ThemeMusicOscillator;
    commit(m);
  };
  scopeSelect.onchange = () => {
    const m = music();
    m.scope = scopeSelect.value as ThemeMusicScope;
    commit(m);
  };
  barsMinus.onclick = () => {
    const m = music();
    if (m.bars <= 1) return;
    m.bars -= 1;
    const maxStep = themeMusicTotalSteps(m) - 1;
    m.cells = m.cells.filter((c) => c.step <= maxStep);
    commit(m);
  };
  barsPlus.onclick = () => {
    const m = music();
    if (m.bars >= 8) return;
    m.bars += 1;
    commit(m);
  };
  clearBtn.onclick = () => {
    const m = music();
    m.cells = [];
    commit(m);
  };
  playBtn.onclick = () => {
    const m = music();
    if (player.playing) {
      player.stop();
      syncControls();
      renderGrid();
      return;
    }
    if (!m.cells.length) {
      m.enabled = true;
      // allow play of silence? skip
      syncControls();
      return;
    }
    m.enabled = true;
    opts.setMusic(m);
    player.start(m, () => {
      renderGrid();
    });
    syncControls();
  };

  syncControls();
  renderGrid();

  return {
    getMusic: () => music(),
    setMusic: (next) => {
      opts.setMusic(cloneMusic(next));
      if (player.playing) {
        player.updateMusic(next);
      }
      syncControls();
      renderGrid();
    },
    destroy: () => {
      player.dispose();
      box.remove();
    },
  };
}
